import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import jwtDecode from 'jwt-decode';
import OAuthService from 'src/app/auth/services/oAuthService';

const BASE = process.env.REACT_APP_RESOURCE_API_KEY_URL;
const SPECIAL_COUNT_URL = `${BASE}api/specialDiscounts/search/countByTypeAndStatus`;
const PROJECT_COUNT_URL = `${BASE}api/projectDiscounts/search/countByStatus`;

const isMDUser = () => {
  try {
    const raw = localStorage.getItem('token') || localStorage.getItem('access_token');
    const decoded = raw ? jwtDecode(raw) : null;
    return Array.isArray(decoded?.scope) && decoded.scope.includes('ROLE_MD');
  } catch (err) {
    return false;
  }
};

const toCount = (data) => {
  if (typeof data === 'number') return data;
  if (typeof data === 'string' && data.trim() !== '' && !Number.isNaN(Number(data))) {
    return Number(data);
  }
  if (data && typeof data === 'object') {
    const value = Number(data.count ?? data.total ?? data.totalElements ?? data.PENDING);
    return Number.isNaN(value) ? 0 : value;
  }
  return 0;
};

const fetchSpecialCount = async (type) => {
  const res = await OAuthService.getList(`${SPECIAL_COUNT_URL}?type=${type}&status=PENDING`);
  return toCount(res?.data);
};

const fetchProjectCount = async () => {
  const res = await OAuthService.getList(`${PROJECT_COUNT_URL}?status=PENDING`);
  return toCount(res?.data);
};

const valueOf = (result) => {
  if (result.status === 'fulfilled') return result.value;
  console.warn('[notificationPanel] pending count request failed', result.reason);
  return 0;
};

const buildSpecialItem = (type, tab, count) => ({
  id: `pending-special-discount-${type.toLowerCase()}`,
  icon: 'heroicons-outline:tag',
  title: `Special Discount (${type})`,
  description: `<b>${count}</b> pending your approval`,
  count,
  variant: 'default',
  link: `/special-discount?tab=${tab}&specialDiscType=${type}&specialDiscStatus=PENDING`,
  useRouter: true,
});

const buildItems = ({ project, sdr, sdp }) => {
  const items = [];

  if (project > 0) {
    items.push({
      id: 'pending-project-discount',
      icon: 'heroicons-outline:clipboard-check',
      title: 'Project Forecast Discount',
      description: `<b>${project}</b> pending your approval`,
      count: project,
      variant: 'default',
      link: '/discountApproval',
      useRouter: true,
    });
  }

  if (sdr > 0) items.push(buildSpecialItem('SDR', 0, sdr));
  if (sdp > 0) items.push(buildSpecialItem('SDP', 1, sdp));

  return items;
};

export const getPendingApprovals = createAsyncThunk(
  'notificationPanel/getPendingApprovals',
  async () => {
    if (!isMDUser()) return [];

    const [project, sdr, sdp] = await Promise.allSettled([
      fetchProjectCount(),
      fetchSpecialCount('SDR'),
      fetchSpecialCount('SDP'),
    ]);

    return buildItems({ project: valueOf(project), sdr: valueOf(sdr), sdp: valueOf(sdp) });
  },
  {
    condition: (_, { getState }) => !getState().notificationPanel?.data?.loading,
  }
);

const notificationsAdapter = createEntityAdapter({});

const initialState = notificationsAdapter.getInitialState({ loading: false });

export const { selectAll: selectNotifications, selectById: selectNotificationsById } =
  notificationsAdapter.getSelectors((state) => state.notificationPanel.data);

export const selectPendingTotal = (state) =>
  selectNotifications(state).reduce((sum, item) => sum + (item.count || 0), 0);

const dataSlice = createSlice({
  name: 'notificationPanel/data',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getPendingApprovals.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPendingApprovals.fulfilled, (state, action) => {
        state.loading = false;
        notificationsAdapter.setAll(state, action.payload);
      })
      .addCase(getPendingApprovals.rejected, (state) => {
        state.loading = false;
      });
  },
});

export default dataSlice.reducer;
